"use client";

import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Logistics + Insurance picker — two-column composer placed between the
 * proposal-accepted moment and the contract-capture moment. Lets the
 * arranging party (seller for CIF, buyer for FOB) pick a carrier and an
 * insurance policy with loss-payee in the LC's beneficiary chain.
 *
 *   T2 (CIF, Aurora arranges, FAB loss-payee)  → screen 4.6
 *   T3 (FOB, Pampagro arranges, Itaú loss-payee) → screen 5.5
 *
 * Reads as a settled choice — the demo doesn't actually wait for input.
 * The "selected" rows light up to indicate the audience-visible decision.
 */

type Carrier = {
  name: string;
  modality: string;
  etaDays: number;
  onTimePct: number;
  selected: boolean;
};

type Insurer = {
  name: string;
  policyTemplate: string;
  selected: boolean;
};

export function LogisticsInsurancePicker({
  incoterms,
  arrangedBy,
  lossPayee,
  carriers,
  insurers,
  coveragePct = 110,
  cifBasisUsd,
  interactiveInsurerPick = false,
  hideCompositionBlock = false,
  insuranceBadgeStyle = "auto",
  modality = "sea",
  className,
}: {
  incoterms: "CIF" | "FOB" | "CIP";
  arrangedBy: string;
  lossPayee: string;
  carriers: Carrier[];
  insurers: Insurer[];
  coveragePct?: number;
  cifBasisUsd: number;
  /** When true, the insurer rows render with a "View" CTA instead of the
   *  pre-selected tick. Clicking View opens a modal with the insurer's
   *  policy details; pressing Select inside the modal flips the row to
   *  selected ✓. Used on T2's 4.4 to dramatize the picker beat. */
  interactiveInsurerPick?: boolean;
  /** When true, drop the post-picker "Letter of Credit composition" /
   *  "Buyer-arranged · FOB" heading + the Incoterms / Coverage field grid
   *  beneath it. T2's 4.4 sets this so LC composition lives in the
   *  dedicated progress card on 4.5 / 4.12 / 4.13 instead. */
  hideCompositionBlock?: boolean;
  /** "auto" (default) reads the badge style from `incoterms` —
   *  state-financed bright for CIF, muted for FOB. "mandatory" forces
   *  the bright styling regardless of incoterms. T3's 5.4 uses
   *  "mandatory" so its insurance badge matches T2 4.4 visually even
   *  though T3 is FOB. */
  insuranceBadgeStyle?: "auto" | "mandatory";
  /** "sea" (default) renders the carrier visual as a container ship and
   *  the insurance column as "Marine Cargo Insurance". "air" swaps in an
   *  aircraft illustration with the carrier's logo on the fuselage and
   *  retitles the insurance column to "Air Cargo Insurance". Used on
   *  T4's 7.4. */
  modality?: "sea" | "air";
  className?: string;
}) {
  const coverageUsd = cifBasisUsd * (coveragePct / 100);
  const insuranceMandatory = incoterms !== "FOB";
  const badgeBright = insuranceBadgeStyle === "mandatory" || insuranceMandatory;
  // Track which insurer is "selected" in interactive mode. Starts as the
  // candidate that came in selected from the data (so the rest of the
  // picker reads consistently); the row hides the tick until the user
  // confirms via the modal.
  const initialSelected = insurers.find((i) => i.selected)?.name ?? null;
  const [pickedInsurer, setPickedInsurer] = useState<string | null>(
    interactiveInsurerPick ? null : initialSelected,
  );
  const [viewedInsurer, setViewedInsurer] = useState<string | null>(null);
  const isSelected = (name: string) =>
    interactiveInsurerPick ? pickedInsurer === name : insurers.find((i) => i.name === name)?.selected === true;
  const t = useTranslations("logisticsInsurancePicker");
  return (
    <div className={clsx("grid h-full grid-cols-2 gap-4", className)}>
      {/* ------ Logistics column ------ */}
      <div className="glass flex flex-col p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">{t("carrierColumnKicker")}</p>
            <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t("carrierColumnHeader")}</h3>
          </div>
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-traced">
            {t("arrangedByPrefix")} <span dir="ltr">{arrangedBy}</span>
          </span>
        </div>

        {/* Exporter-side picker action — exporter can either upload their
         *  own carrier paperwork OR pick from the Orbitra-verified
         *  shortlist below. */}
        <div className="mt-3 flex flex-col gap-1.5">
          <button
            type="button"
            className="w-full rounded-md bg-state-traced px-3 py-2 font-display text-[11px] font-semibold uppercase tracking-wider text-orbitra-ink shadow-[0_0_18px_var(--state-traced-glow)] hover:bg-state-traced/90"
          >
            {t("uploadDocuments")}
          </button>
          <p className="text-[10px] text-orbitra-muted">
            {t("uploadHelper")}
          </p>
        </div>

        <ul className="mt-3 flex flex-col gap-2 text-[12px]">
          {carriers.map((c) => (
            <li
              key={c.name}
              className={clsx(
                "flex items-center gap-3 rounded-md border px-3 py-2",
                c.selected
                  ? "border-state-traced/50 bg-state-traced/10 text-state-traced shadow-[0_0_18px_var(--state-traced-glow)]"
                  : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-paper",
              )}
            >
              <span className="font-mono text-[10px] opacity-80" dir="ltr">{c.modality}</span>
              <span className="flex-1 font-semibold" dir="ltr">{c.name}</span>
              <span className="font-mono text-[10px]">{t("etaPrefix")} {c.etaDays} {t("etaUnitDays")}</span>
              <span className="font-mono text-[10px]">{t("onTimePrefix")} {c.onTimePct}%</span>
              {c.selected && <span className="font-mono text-[10px] uppercase tracking-wider">{t("selectedTag")}</span>}
            </li>
          ))}
        </ul>

        {/* Visual carrier — branded for the selected carrier. Ship for sea
         *  modality (Maersk on T2), aircraft for air (Qatar Airways Cargo
         *  on T4). */}
        {modality === "air" ? (
          <CarrierAircraft carrier={carriers.find((c) => c.selected)?.name ?? carriers[0]?.name ?? ""} />
        ) : (
          <CarrierShip carrier={carriers.find((c) => c.selected)?.name ?? carriers[0]?.name ?? ""} />
        )}

        <div className="mt-auto rounded-md border border-state-traced/20 bg-orbitra-ink/40 px-3 py-2 text-[10px] text-orbitra-muted">
          {t("atlasFooter")}
        </div>
      </div>

      {/* ------ Insurance column ------ */}
      <div className="glass flex flex-col p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              {modality === "air" ? t("insuranceColumnKickerAir") : t("insuranceColumnKickerSea")}
            </p>
            <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t("insuranceColumnHeader")}</h3>
          </div>
          <span className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wider",
            badgeBright
              ? "border-state-financed/40 bg-state-financed/10 text-state-financed"
              : "border-orbitra-muted/40 bg-orbitra-muted/10 text-orbitra-muted",
          )}>
            {t("arrangedByPrefix")} <span dir="ltr">{arrangedBy}</span>
          </span>
        </div>

        <ul className="mt-3 flex flex-col gap-2 text-[12px]">
          {insurers.map((i) => {
            const selected = isSelected(i.name);
            // In interactive mode, every row exposes a "View" CTA — the
            // candidate becomes "selected ✓" only after the user
            // confirms inside the modal.
            return (
              <li
                key={i.name}
                className={clsx(
                  "flex items-center gap-3 rounded-md border px-3 py-2",
                  selected
                    ? "border-state-financed/50 bg-state-financed/10 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]"
                    : "border-orbitra-muted/30 bg-orbitra-ink/40 text-orbitra-paper",
                )}
              >
                <span className="flex-1 font-semibold" dir="ltr">{i.name}</span>
                <span className="font-mono text-[10px] opacity-80" dir="ltr">{i.policyTemplate}</span>
                {interactiveInsurerPick ? (
                  selected ? (
                    <span className="font-mono text-[10px] uppercase tracking-wider">{t("selectedTag")}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setViewedInsurer(i.name)}
                      className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed hover:bg-state-financed/20"
                    >
                      {t("viewCta")}
                    </button>
                  )
                ) : (
                  selected && <span className="font-mono text-[10px] uppercase tracking-wider">{t("selectedTag")}</span>
                )}
              </li>
            );
          })}
        </ul>

        {interactiveInsurerPick && viewedInsurer && (
          <InsurerDetailModal
            insurer={insurers.find((i) => i.name === viewedInsurer)!}
            incoterms={incoterms}
            coveragePct={coveragePct}
            coverageUsd={coverageUsd}
            lossPayee={lossPayee}
            onSelect={() => {
              setPickedInsurer(viewedInsurer);
              setViewedInsurer(null);
            }}
            onClose={() => setViewedInsurer(null)}
          />
        )}

        {!hideCompositionBlock && (
          <>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              {insuranceMandatory ? t("compositionHeaderMandatory") : t("compositionHeaderFob")}
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
              <Field label={t("fieldIncoterms")}               value={incoterms} />
              <Field label={t("fieldInsuranceBeneficiary")}    value={lossPayee} />
              <Field label={t("fieldCoverage")}                value={`${t("coverageDescPrefix")} ${coveragePct}% ${t("coverageDescSuffix")}`} />
              <Field label={t("fieldCoverageAmount")}          value={`USD ${Math.round(coverageUsd).toLocaleString()}`} />
            </div>
          </>
        )}

        <div className="mt-auto rounded-md border border-state-financed/20 bg-orbitra-ink/40 px-3 py-2 text-[10px] text-orbitra-muted">
          {t("insurerFooter")} <span dir="ltr">{lossPayee}</span>{t("insurerFooterTail")}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-state-financed/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className="mt-0.5 text-orbitra-paper">{value}</p>
    </div>
  );
}

/**
 * InsurerDetailModal — centered popup that surfaces an insurer's policy
 * details on T2's 4.4 picker. Select (primary, autofocused) flips the
 * row to selected ✓ in the parent; Close just dismisses.
 */
function InsurerDetailModal({
  insurer,
  incoterms,
  coveragePct,
  coverageUsd,
  lossPayee,
  onSelect,
  onClose,
}: {
  insurer: Insurer;
  incoterms: "CIF" | "FOB" | "CIP";
  coveragePct: number;
  coverageUsd: number;
  lossPayee: string;
  onSelect: () => void;
  onClose: () => void;
}) {
  const selectBtnRef = useRef<HTMLButtonElement>(null);
  const t = useTranslations("logisticsInsurancePicker");

  useEffect(() => {
    selectBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="absolute inset-0 z-30 grid place-items-center bg-orbitra-ink/55 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="insurer-detail-title"
        className="glass w-[min(520px,92vw)] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
              {t("insurerModalKicker")}
            </p>
            <h3 id="insurer-detail-title" className="mt-1 font-display text-base font-semibold text-orbitra-paper" dir="ltr">
              {insurer.name}
            </h3>
          </div>
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-state-financed">
            {t("insurerModalShortlistTag")}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
          <Field label={t("insurerModalInsurer")}          value={insurer.name} />
          <Field label={t("insurerModalPolicy")}           value={insurer.policyTemplate} />
          <Field label={t("fieldIncoterms")}               value={incoterms} />
          <Field label={t("fieldCoverage")}                value={`${t("coverageDescPrefix")} ${coveragePct}% ${t("coverageDescSuffix")}`} />
          <Field label={t("fieldCoverageAmount")}          value={`USD ${Math.round(coverageUsd).toLocaleString()}`} />
          <Field label={t("fieldInsuranceBeneficiary")}    value={lossPayee} />
        </div>

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-orbitra-muted/40 bg-orbitra-ink/40 px-4 py-2 font-display text-sm font-semibold text-orbitra-paper hover:border-orbitra-muted/70"
          >
            {t("insurerModalClose")}
          </button>
          <button
            ref={selectBtnRef}
            type="button"
            onClick={onSelect}
            className="cta-primary px-5 py-2 font-display text-sm font-semibold"
          >
            {t("insurerModalSelect")}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * CarrierShip — small SVG visual of a container ship with the selected
 * carrier's brand mark. Sits below the carrier list as a "this is what
 * we're booking" cue. Maersk uses the carrier's signature Maersk-blue;
 * other carriers fall back to a neutral state-traced styling.
 */
function CarrierShip({ carrier }: { carrier: string }) {
  // Extract the short brand name (e.g. "Maersk · Triton" → "Maersk").
  const brand = carrier.split("·")[0]?.trim() ?? carrier;
  const isMaersk = /maersk/i.test(brand);
  // Maersk's iconic seven-pointed star is famous; we render a stylised
  // "M" wordmark on a Maersk-blue band over a teal sea. For non-Maersk
  // selections we keep the same shape but use brand-teal accents.
  const hull       = isMaersk ? "#0073B7" : "var(--state-traced)";
  const hullDeep   = isMaersk ? "#005490" : "var(--orbitra-teal-deep)";
  const wordmark   = isMaersk ? "MAERSK"  : brand.toUpperCase().slice(0, 8);
  const t = useTranslations("logisticsInsurancePicker");
  return (
    <div className="mt-3 rounded-md border border-state-traced/30 bg-orbitra-ink/40 p-3">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
          {t("bookedVesselPrefix")} <span dir="ltr">{brand}</span>
        </p>
        <p className="font-mono text-[9px] text-orbitra-muted">{t("vesselSubtag")}</p>
      </div>
      <div className="relative mt-2 h-20 overflow-hidden rounded-md">
        {/* Sea band */}
        <div className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-orbitra-teal-deep/60 to-orbitra-ink/80" />
        {/* Wakes */}
        <div className="absolute bottom-2 left-3 right-3 h-px bg-state-traced/30" />
        <div className="absolute bottom-3.5 left-8 right-8 h-px bg-state-traced/20" />
        {/* Ship SVG */}
        <svg viewBox="0 0 320 80" className="absolute inset-0 h-full w-full">
          {/* Hull (curved trapezoid) */}
          <path
            d="M 40 56 L 280 56 L 264 70 Q 252 76 240 76 L 80 76 Q 64 76 56 70 Z"
            fill={hull}
          />
          <path d="M 40 56 L 280 56 L 280 60 L 40 60 Z" fill={hullDeep} opacity="0.6" />
          {/* Bridge / superstructure */}
          <rect x="218" y="38" width="40" height="20" fill={hull} />
          <rect x="222" y="42" width="6" height="6" fill={hullDeep} opacity="0.7" />
          <rect x="232" y="42" width="6" height="6" fill={hullDeep} opacity="0.7" />
          <rect x="242" y="42" width="6" height="6" fill={hullDeep} opacity="0.7" />
          {/* Funnel */}
          <rect x="262" y="32" width="10" height="14" fill={hullDeep} />
          <rect x="262" y="32" width="10" height="3" fill={hull} />
          {/* Containers stacked along the deck */}
          {[60, 78, 96, 114, 132, 150, 168, 186, 204].map((x, i) => (
            <g key={x}>
              <rect x={x} y="42" width="16" height="14" fill={i % 3 === 0 ? "#D14B3D" : i % 3 === 1 ? "#1A8A6F" : "#E2A23B"} />
              <rect x={x} y="42" width="16" height="2" fill="rgba(0,0,0,0.25)" />
              {i % 2 === 0 && (
                <rect x={x} y="30" width="16" height="12" fill={i % 3 === 0 ? "#1A8A6F" : "#E2A23B"} />
              )}
            </g>
          ))}
          {/* Brand wordmark on the hull */}
          <text
            x="160"
            y="68"
            textAnchor="middle"
            fontFamily="ui-monospace, SFMono-Regular, monospace"
            fontSize="9"
            fontWeight="700"
            letterSpacing="2"
            fill="#FFFFFF"
            opacity="0.92"
          >
            {wordmark}
          </text>
          {/* Maersk seven-pointed star on the funnel (stylised) */}
          {isMaersk && (
            <text x="267" y="42" textAnchor="middle" fontSize="9" fill="#FFFFFF">★</text>
          )}
        </svg>
      </div>
    </div>
  );
}

/**
 * CarrierAircraft — air-cargo equivalent of CarrierShip. Side-profile
 * SVG of a widebody freighter (B777F / A330F silhouette) with the
 * selected carrier's brand mark on the fuselage. Qatar Airways Cargo
 * uses its maroon livery + the carrier logo PNG anchored over the
 * fuselage door; other carriers fall back to a state-traced styling.
 */
function CarrierAircraft({ carrier }: { carrier: string }) {
  const brand = carrier.split("·")[0]?.trim() ?? carrier;
  const isQatar   = /qatar/i.test(brand);
  const isEmirates = /emirates/i.test(brand);
  const isEtihad  = /etihad/i.test(brand);
  // Qatar Airways Cargo: signature maroon (#5C0931). Emirates: red (#D71920).
  // Etihad: bronze (#BD8B13). Others: state-traced.
  const livery     = isQatar    ? "#5C0931"
                    : isEmirates ? "#D71920"
                    : isEtihad   ? "#BD8B13"
                    : "var(--state-traced)";
  const fuselage   = "#F2EDE4";  // off-white widebody fuselage
  const wordmark   = isQatar    ? "QATAR AIRWAYS CARGO"
                    : isEmirates ? "EMIRATES SKYCARGO"
                    : isEtihad   ? "ETIHAD CARGO"
                    : brand.toUpperCase().slice(0, 18);
  // Logo files. Currently only Qatar Airways Cargo has a real PNG; the
  // other carriers fall back to a text wordmark on the tail.
  const logoSrc    = isQatar ? "/carriers/qatar-airways-cargo.png" : null;
  const t = useTranslations("logisticsInsurancePicker");
  return (
    <div className="mt-3 rounded-md border border-state-traced/30 bg-orbitra-ink/40 p-3">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
          {t("bookedFlightPrefix")} <span dir="ltr">{brand}</span>
        </p>
        <p className="font-mono text-[9px] text-orbitra-muted">{t("flightSubtag")}</p>
      </div>
      <div className="relative mt-2 h-20 overflow-hidden rounded-md bg-gradient-to-b from-orbitra-teal-deep/30 to-orbitra-ink/70">
        {/* Subtle horizon line */}
        <div className="absolute inset-x-0 top-1/2 h-px bg-state-traced/20" />
        {/* Contrail */}
        <div className="absolute right-6 top-[44%] h-px w-40 bg-gradient-to-l from-transparent via-state-traced/40 to-state-traced/10" />
        {/* Aircraft SVG */}
        <svg viewBox="0 0 320 80" className="absolute inset-0 h-full w-full">
          {/* Wing (rear, behind fuselage) — swept-back delta */}
          <path
            d="M 130 42 L 200 42 L 235 56 L 205 56 L 175 48 L 130 48 Z"
            fill={livery}
            opacity="0.85"
          />
          {/* Horizontal stabiliser (rear small wing) */}
          <path
            d="M 38 38 L 70 38 L 82 44 L 50 44 Z"
            fill={livery}
            opacity="0.7"
          />
          {/* Fuselage (cylindrical body, nose right) */}
          <path
            d="M 40 40 Q 40 32 60 32 L 240 32 Q 270 32 280 40 Q 270 48 240 48 L 60 48 Q 40 48 40 40 Z"
            fill={fuselage}
          />
          {/* Fuselage shading underline */}
          <path d="M 60 47 L 240 47 Q 268 47 278 41 Q 270 43 240 43 L 60 43 Q 42 43 41 41 Q 42 47 60 47 Z" fill="rgba(0,0,0,0.08)" />
          {/* Cockpit window */}
          <path d="M 270 36 Q 276 36 278 39 L 268 39 Z" fill="#0E1620" />
          {/* Cabin windows row (small dots) */}
          {[80, 95, 110, 125, 140, 155, 170, 185, 200, 215, 230].map((x) => (
            <rect key={x} x={x} y="37" width="3" height="2" fill="#0E1620" opacity="0.7" />
          ))}
          {/* Engine — pylon + nacelle slung under the wing */}
          <rect x="178" y="48" width="2" height="6" fill={livery} opacity="0.6" />
          <ellipse cx="184" cy="56" rx="14" ry="4" fill="#1A2330" />
          <ellipse cx="172" cy="56" rx="2" ry="2.5" fill="#0E1620" />
          {/* Vertical tail fin with carrier livery + wordmark */}
          <path d="M 50 32 L 38 12 L 56 12 L 62 32 Z" fill={livery} />
          {/* Tail-fin highlight for Qatar's oryx silhouette — small white sliver */}
          {isQatar && (
            <path d="M 46 26 L 52 14 L 56 14 L 52 26 Z" fill="#FFFFFF" opacity="0.55" />
          )}
          {/* Fuselage wordmark — small, restrained */}
          <text
            x="160"
            y="42"
            textAnchor="middle"
            fontFamily="ui-serif, Georgia, serif"
            fontSize="7"
            fontWeight="700"
            letterSpacing="1.4"
            fill={livery}
            opacity="0.88"
          >
            {wordmark}
          </text>
        </svg>
        {/* Carrier logo badge — anchored bottom-right; only Qatar has a
         *  real PNG today. */}
        {logoSrc && (
          <div className="absolute bottom-1 right-1 flex items-center gap-1 rounded-sm bg-orbitra-ink/70 px-1 py-0.5">
            <img
              src={logoSrc}
              alt={`${brand} logo`}
              className="h-6 w-6 object-contain"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </div>
  );
}
